/**
 * Client-side routing inside the Work Chats ("Connect") surface.
 *
 * Every `/[orgSlug]/chats/...` page renders the same pathname-driven view
 * (`WorkChatsRouteView`), so moving between channels, DMs, group DMs, the
 * inbox feeds and the index only needs the URL to change. These helpers do that
 * with the History API, which Next's App Router syncs into `usePathname` /
 * `useSearchParams` without a server request (see `node_modules/next/dist/docs/
 * 01-app/01-getting-started/04-linking-and-navigating.md`, "Native History
 * API"). Anything that leaves the surface — another workspace, another page,
 * another origin — returns `null` here and goes through the Next router.
 */

export type WorkChatsInboxFilter = 'threads' | 'mentions';

export type WorkChatsRoute =
  | { kind: 'index'; orgSlug: string }
  | { kind: 'channel'; orgSlug: string; channelId: string }
  | { kind: 'inbox'; orgSlug: string; filter: WorkChatsInboxFilter };

function decodeSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * Parse a pathname into a Work Chats route, mirroring the file-system routes:
 * `/[org]/chats`, `/[org]/chats/[channelId]`, `/[org]/chats/inbox/[filter]`.
 * Returns null for anything else.
 */
export function parseWorkChatsPath(pathname: string): WorkChatsRoute | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2 || segments[1] !== 'chats') return null;
  const orgSlug = decodeSegment(segments[0] ?? '');
  if (!orgSlug) return null;
  const rest = segments.slice(2);
  if (rest.length === 0) return { kind: 'index', orgSlug };
  if (rest.length === 2 && rest[0] === 'inbox') {
    // Same fallback as the inbox page always had: unknown filters → threads.
    const filter = rest[1] === 'mentions' ? 'mentions' : 'threads';
    return { kind: 'inbox', orgSlug, filter };
  }
  if (rest.length === 1) {
    const channelId = decodeSegment(rest[0] ?? '');
    return channelId ? { kind: 'channel', orgSlug, channelId } : null;
  }
  return null;
}

/**
 * Decide whether `targetHref` can be reached from `currentHref` by rewriting
 * history in place. Returns the same-origin URL (path + search + hash) to hand
 * to `pushState` / `replaceState`, or null when the Next router must handle it.
 */
export function planWorkChatsNavigation(
  currentHref: string,
  targetHref: string
): string | null {
  let current: URL;
  let target: URL;
  try {
    current = new URL(currentHref);
    target = new URL(targetHref, current);
  } catch {
    return null;
  }
  if (target.origin !== current.origin) return null;
  const from = parseWorkChatsPath(current.pathname);
  const to = parseWorkChatsPath(target.pathname);
  if (!from || !to || from.orgSlug !== to.orgSlug) return null;
  return `${target.pathname}${target.search}${target.hash}`;
}

export type WorkChatsHistoryMode = 'push' | 'replace';

/**
 * Navigate within Work Chats without a server round trip. Returns false (and
 * does nothing) when the target is outside the current workspace's chats tree,
 * so the caller falls back to the Next router.
 */
export function navigateWorkChatsInPlace(
  href: string,
  mode: WorkChatsHistoryMode = 'push'
): boolean {
  if (typeof window === 'undefined') return false;
  const url = planWorkChatsNavigation(window.location.href, href);
  if (url === null) return false;
  const { pathname, search, hash } = window.location;
  // Re-clicking the open chat must not stack duplicate history entries.
  if (mode === 'push' && url === `${pathname}${search}${hash}`) return true;
  // Next patches these methods to copy its router state into the entry and
  // sync `usePathname` / `useSearchParams` (and popstate restores from it).
  if (mode === 'push') window.history.pushState(null, '', url);
  else window.history.replaceState(null, '', url);
  return true;
}

/** Plain primary-button click with no modifier that would open a new tab or
 *  window, download, or otherwise expect the browser's default behaviour. */
export function isPlainLeftClick(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
  currentTarget: { getAttribute(name: string): string | null };
}): boolean {
  if (event.defaultPrevented || event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }
  const target = event.currentTarget.getAttribute('target');
  if (target && target !== '_self') return false;
  return event.currentTarget.getAttribute('download') === null;
}
